import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Stack,
  Paper,
  LinearProgress,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  BarChart,
  Star,
  History,
  Timeline,
} from '@mui/icons-material';
import { maskData } from '../FieldData';

const formatToDDMMYYYY = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}-${month}-${year}`;
};

const ClientAnalysis = ({ selectedItem, allData, branchData }) => {
  if (!selectedItem) return null;

  // 1. Filter all records for this specific client using normalized trimmed numbers
  const targetNumber = selectedItem.client?.number?.toString().trim();
  const clientHistory = allData.filter(item => 
    item.client?.number?.toString().trim() === targetNumber
  );
  
  // 2. Use Analytics from selectedItem.analysis
  const totalSpent = selectedItem.analysis?.lifetimeValue || 0;
  const avgBill = selectedItem.analysis?.avgBill || 0;
  const topServices = selectedItem.analysis?.topMostTakenServices || [];
  const topStaff = selectedItem.analysis?.topPreferredStaff || [];
  const mostTakenService = topServices[0]?.name || selectedItem.analysis?.mostTakenService || 'N/A';
  const preferredStaff = topStaff[0]?.name || selectedItem.analysis?.preferredStaff || 'Any';
  const visitCount = selectedItem.analysis?.visitCount || 1;

  const trendData = selectedItem.analysis?.spendingTrend?.map(item => ({ dateOfBilling: item.date, totalAmount: item.amount })) || [];
  const maxTrendAmount = Math.max(...trendData.map(t => t.totalAmount), 1);

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* row 1: Profile & Core Stats */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" fontWeight="700" gutterBottom color="primary">Customer Profile</Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: '#fafafa' }}>
            <Stack spacing={1.5}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">Full Name:</Typography>
                <Typography variant="body2" fontWeight="600">{selectedItem.client?.name}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">Mobile:</Typography>
                <Typography variant="body2" fontWeight="600" sx={{ letterSpacing: 0.5 }}>
                  {typeof branchData?.isMasked !== 'boolean' || !selectedItem.client?.number
                    ? 'loading...'
                    : maskData(selectedItem.client?.number, branchData?.isMasked)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">Gender:</Typography>
                <Typography variant="body2" fontWeight="600" sx={{ textTransform: 'capitalize' }}>{selectedItem.client?.gender}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" fontWeight="700" gutterBottom color="primary">Current Transaction</Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: '#fafafa' }}>
            <Stack spacing={1.5}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">Date:</Typography>
                <Typography variant="body2" fontWeight="600">{formatToDDMMYYYY(selectedItem.dateOfBilling)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">Timestamp:</Typography>
                <Typography variant="body2" fontWeight="600">{selectedItem.timeOfBilling}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">Total Billing:</Typography>
                <Typography variant="body2" fontWeight="800" color="secondary">₹{selectedItem.totalAmount}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* row 2: Advanced Analytics Cards */}
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="800" sx={{ mt: 1, mb: 2, color: '#333', display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarChart color="secondary" /> Deep Behavioral Analytics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ height: '100%', p: 2, bgcolor: '#f0f4ff', border: '1px solid #d0d9ff', textAlign: 'center', borderRadius: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="caption" color="textSecondary" fontWeight="700" sx={{ textTransform: 'uppercase' }}>Most Taken Service</Typography>
                <Typography variant="h6" fontWeight="800" color="#3f51b5">{mostTakenService}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ height: '100%', p: 2, bgcolor: '#fff4f4', border: '1px solid #ffd0d0', textAlign: 'center', borderRadius: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="caption" color="textSecondary" fontWeight="700" sx={{ textTransform: 'uppercase' }}>Lifetime Value</Typography>
                <Typography variant="h6" fontWeight="800" color="#d32f2f">₹{totalSpent.toLocaleString()}</Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>Avg: ₹{avgBill} / bill</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ height: '100%', p: 2, bgcolor: '#f1fdf4', border: '1px solid #c8e6c9', textAlign: 'center', borderRadius: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="caption" color="textSecondary" fontWeight="700" sx={{ textTransform: 'uppercase' }}>Preferred Staff</Typography>
                <Typography variant="h6" fontWeight="800" color="#2e7d32">{preferredStaff}</Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>{selectedItem.analysis?.visitCount || 0} Total Visits</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* row 3: Top Preferences (Top 3) split into two distinct cards */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {/* Top Services Card */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="700" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Star fontSize="small" sx={{ color: '#ffb400' }} /> Top Service Preferences
              </Typography>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '20px', height: '220px', overflow: 'auto', border: '1px solid #e0e6ff', bgcolor: '#f8faff', transition: '0.3s', '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.08)' } }}>
                <Stack spacing={2.5}>
                  {topServices.slice(0, 3).map((item, idx) => (
                    <Box key={idx}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="caption" fontWeight="700" sx={{ fontSize: '12px', color: '#1a237e', display: 'flex', alignItems: 'center', gap: 1 }} noWrap>
                          <Box sx={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: idx === 0 ? '#ffd700' : idx === 1 ? '#c0c0c0' : '#cd7f32', color: '#fff', fontSize: '10px', fontWeight: '900' }}>
                            {idx + 1}
                          </Box>
                          {item.name}
                        </Typography>
                        <Chip label={`${item.count} Visits`} size="small" sx={{ height: 20, fontSize: '10px', fontWeight: '800', bgcolor: '#fff', border: '1px solid #e0e6ff', color: '#3f51b5' }} />
                      </Stack>
                      <LinearProgress 
                        variant="determinate" 
                        value={(item.count / visitCount) * 100} 
                        sx={{ height: 8, borderRadius: 10, bgcolor: '#e0e6ff', '& .MuiLinearProgress-bar': { borderRadius: 10, background: 'linear-gradient(90deg, #1a237e 0%, #6174DD 100%)' } }} 
                      />
                    </Box>
                  ))}
                  {topServices.length === 0 && <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', textAlign: 'center', mt: 4 }}>No service data available</Typography>}
                </Stack>
              </Paper>
            </Grid>

            {/* Top Staff Card */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="700" gutterBottom color="secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Star fontSize="small" sx={{ color: '#ffb400' }} /> Top Preferred Staff
              </Typography>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '20px', height: '220px', overflow: 'auto', border: '1px solid #fce4ec', bgcolor: '#fff5f8', transition: '0.3s', '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.08)' } }}>
                <Stack spacing={2.5}>
                  {topStaff.slice(0, 3).map((item, idx) => (
                    <Box key={idx}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="caption" fontWeight="700" sx={{ fontSize: '12px', color: '#880e4f', display: 'flex', alignItems: 'center', gap: 1 }} noWrap>
                          <Box sx={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: idx === 0 ? '#ffd700' : idx === 1 ? '#c0c0c0' : '#cd7f32', color: '#fff', fontSize: '10px', fontWeight: '900' }}>
                            {idx + 1}
                          </Box>
                          {item.name}
                        </Typography>
                        <Chip label={`${item.count} Visits`} size="small" sx={{ height: 20, fontSize: '10px', fontWeight: '800', bgcolor: '#fff', border: '1px solid #fce4ec', color: '#c2185b' }} />
                      </Stack>
                      <LinearProgress 
                        variant="determinate" 
                        value={(item.count / visitCount) * 100} 
                        color="secondary"
                        sx={{ height: 8, borderRadius: 10, bgcolor: '#fce4ec', '& .MuiLinearProgress-bar': { borderRadius: 10, background: 'linear-gradient(90deg, #c2185b 0%, #f06292 100%)' } }} 
                      />
                    </Box>
                  ))}
                  {topStaff.length === 0 && <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', textAlign: 'center', mt: 4 }}>No staff data available</Typography>}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* row 4: Spending Trend (Last 5 Visits) */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight="700" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Timeline fontSize="small" /> Spending Trend (Last 5 Visits)
          </Typography>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              borderRadius: '12px', 
              height: '200px', 
              display: 'flex', 
              alignItems: 'flex-end', 
              justifyContent: 'space-around', 
              gap: 2,
              background: 'linear-gradient(to top, #f8faff 0%, #ffffff 100%)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {trendData.map((data, idx) => {
              const isLatest = idx === trendData.length - 1;
              return (
                <Box key={idx} sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1 }}>
                  <Tooltip title={`₹${data.totalAmount} on ${data.dateOfBilling}`} arrow placement="top">
                    <Box sx={{ 
                      width: '100%',
                      maxWidth: '35px',
                      height: `${(data.totalAmount / maxTrendAmount) * 120}px`, 
                      background: isLatest 
                        ? 'linear-gradient(180deg, #1a237e 0%, #3f51b5 100%)' 
                        : 'linear-gradient(180deg, #6174DD 0%, #9fa8da 100%)',
                      borderRadius: '6px 6px 2px 2px',
                      boxShadow: isLatest 
                        ? '0 4px 12px rgba(26, 35, 126, 0.3)' 
                        : '0 2px 6px rgba(97, 116, 221, 0.15)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        filter: 'brightness(1.15)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 6px 15px rgba(0,0,0,0.1)'
                      }
                    }} />
                  </Tooltip>
                  <Typography variant="caption" sx={{ fontSize: '10px', fontWeight: '700', mt: 1, display: 'block', color: isLatest ? '#1a237e' : '#777' }}>
                    {data.dateOfBilling?.split('-').slice(1).reverse().join('/')}
                  </Typography>
                </Box>
              );
            })}
            {trendData.length === 0 && <Typography color="textSecondary">No trend data available</Typography>}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight="700" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History fontSize="small" /> Visit History Breakdown
          </Typography>
          <Box sx={{ overflow: 'hidden', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f8faff' }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', color: '#555' }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', color: '#555' }}>Services Taken</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', color: '#555' }}>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {selectedItem.analysis?.spendingTrend?.map((visit, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #e0e0e0' }}>
                    <td style={{ padding: '12px', fontSize: '13px' }}>{formatToDDMMYYYY(visit.date)}</td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {visit.services?.map((s, si) => (
                          <Chip key={si} label={s} size="small" variant="outlined" sx={{ fontSize: '10px', height: '20px' }} />
                        ))}
                      </Stack>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600' }}>₹{visit.amount || 0}</td>
                  </tr>
                ))}
                {(!selectedItem.analysis?.spendingTrend || selectedItem.analysis.spendingTrend.length === 0) && (
                  <tr>
                    <td colSpan={3} style={{ padding: '24px', textAlign: 'center', color: '#777' }}>No visit history found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientAnalysis;
